import { useState, useEffect, useCallback } from 'react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Zap, 
  Package, 
  Plus, 
  Edit2, 
  Trash2, 
  Clock, 
  Calendar,
  Percent,
  Euro,
  Tag,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Gift
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = [
  { id: 'monday', label: 'Lundi' },
  { id: 'tuesday', label: 'Mardi' },
  { id: 'wednesday', label: 'Mercredi' },
  { id: 'thursday', label: 'Jeudi' },
  { id: 'friday', label: 'Vendredi' },
  { id: 'saturday', label: 'Samedi' },
  { id: 'sunday', label: 'Dimanche' }
];

export default function HappyHoursTab() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('happy-hours');
  
  // Data
  const [happyHours, setHappyHours] = useState([]);
  const [packs, setPacks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  
  // Modals
  const [happyHourModal, setHappyHourModal] = useState({ open: false, data: null, isEdit: false });
  const [packModal, setPackModal] = useState({ open: false, data: null, isEdit: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, type: null, id: null, name: '' });
  
  // Loading states
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [happyHoursRes, packsRes, categoriesRes, dishesRes] = await Promise.all([
        axios.get(`${API}/promotions/happy-hours`),
        axios.get(`${API}/promotions/packs`),
        axios.get(`${API}/restaurant-dashboard/menu/categories`),
        axios.get(`${API}/restaurant-dashboard/menu/dishes`).catch(() => ({ data: [] }))
      ]);
      
      setHappyHours(happyHoursRes.data);
      setPacks(packsRes.data);
      setCategories(categoriesRes.data || []);
      setDishes(dishesRes.data || []);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ==================== HAPPY HOURS ====================

  const openHappyHourModal = (happyHour = null) => {
    if (happyHour) {
      setHappyHourModal({
        open: true,
        isEdit: true,
        data: { ...happyHour }
      });
    } else {
      setHappyHourModal({
        open: true,
        isEdit: false,
        data: {
          name: '',
          description: '',
          is_active: true,
          time_slots: [{ day: 'monday', start_time: '15:00', end_time: '18:00' }],
          apply_to: 'all',
          product_ids: [],
          category_ids: [],
          discount_type: 'percentage',
          discount_value: 20,
          start_date: '',
          end_date: ''
        }
      });
    }
  };

  const handleSaveHappyHour = async () => {
    const data = happyHourModal.data;
    
    if (!data.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (!data.time_slots || data.time_slots.length === 0) {
      toast.error('Au moins un créneau horaire est requis');
      return;
    }
    if (!data.discount_value || data.discount_value <= 0) {
      toast.error('La valeur de réduction doit être positive');
      return;
    }
    
    setSaving(true);
    try {
      if (happyHourModal.isEdit) {
        await axios.put(`${API}/promotions/happy-hours/${data.id}`, data);
        toast.success('Happy Hour modifiée');
      } else {
        await axios.post(`${API}/promotions/happy-hours`, data);
        toast.success('Happy Hour créée');
      }
      setHappyHourModal({ open: false, data: null, isEdit: false });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const addTimeSlot = () => {
    setHappyHourModal({
      ...happyHourModal,
      data: {
        ...happyHourModal.data,
        time_slots: [
          ...happyHourModal.data.time_slots,
          { day: 'monday', start_time: '15:00', end_time: '18:00' }
        ]
      }
    });
  };

  const removeTimeSlot = (index) => {
    setHappyHourModal({
      ...happyHourModal,
      data: {
        ...happyHourModal.data,
        time_slots: happyHourModal.data.time_slots.filter((_, i) => i !== index)
      }
    });
  };

  const updateTimeSlot = (index, field, value) => {
    const newSlots = [...happyHourModal.data.time_slots];
    newSlots[index] = { ...newSlots[index], [field]: value };
    setHappyHourModal({
      ...happyHourModal,
      data: { ...happyHourModal.data, time_slots: newSlots }
    });
  };

  // ==================== PACKS ====================

  const openPackModal = (pack = null) => {
    if (pack) {
      setPackModal({
        open: true,
        isEdit: true,
        data: { ...pack }
      });
    } else {
      setPackModal({
        open: true,
        isEdit: false,
        data: {
          name: '',
          description: '',
          is_active: true,
          fixed_items: [],
          choices: [],
          price_type: 'fixed',
          price: '',
          discount_percentage: '',
          available_days: [],
          start_time: '',
          end_time: '',
          max_per_day: '',
          start_date: '',
          end_date: ''
        }
      });
    }
  };

  const handleSavePack = async () => {
    const data = packModal.data;
    
    if (!data.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (data.price_type === 'fixed' && (!data.price || data.price <= 0)) {
      toast.error('Le prix du pack doit être positif');
      return;
    }
    if (data.price_type === 'discount' && (!data.discount_percentage || data.discount_percentage <= 0)) {
      toast.error('Le pourcentage de réduction doit être positif');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...data,
        price: data.price ? parseFloat(data.price) : null,
        discount_percentage: data.discount_percentage ? parseFloat(data.discount_percentage) : null,
        max_per_day: data.max_per_day ? parseInt(data.max_per_day) : null
      };
      
      if (packModal.isEdit) {
        await axios.put(`${API}/promotions/packs/${data.id}`, payload);
        toast.success('Formule modifiée');
      } else {
        await axios.post(`${API}/promotions/packs`, payload);
        toast.success('Formule créée');
      }
      setPackModal({ open: false, data: null, isEdit: false });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const addFixedItem = (dishId) => {
    const dish = dishes.find(d => d.id === dishId);
    if (!dish) return;
    
    const alreadyAdded = packModal.data.fixed_items.some(item => item.id === dishId);
    if (alreadyAdded) {
      toast.error('Ce produit est déjà dans le pack');
      return;
    }
    
    setPackModal({
      ...packModal,
      data: {
        ...packModal.data,
        fixed_items: [
          ...packModal.data.fixed_items,
          { type: 'product', id: dishId, name: dish.name, quantity: 1 }
        ]
      }
    });
  };

  const removeFixedItem = (index) => {
    setPackModal({
      ...packModal,
      data: {
        ...packModal.data,
        fixed_items: packModal.data.fixed_items.filter((_, i) => i !== index)
      }
    });
  };

  // ==================== DELETE ====================

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (deleteModal.type === 'happy-hour') {
        await axios.delete(`${API}/promotions/happy-hours/${deleteModal.id}`);
        toast.success('Happy Hour supprimée');
      } else {
        await axios.delete(`${API}/promotions/packs/${deleteModal.id}`);
        toast.success('Formule supprimée');
      }
      setDeleteModal({ open: false, type: null, id: null, name: '' });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la suppression'));
    } finally {
      setDeleting(false);
    }
  };

  // ==================== TOGGLE ACTIVE ====================

  const toggleHappyHourActive = async (happyHour) => {
    try {
      await axios.put(`${API}/promotions/happy-hours/${happyHour.id}`, {
        is_active: !happyHour.is_active
      });
      fetchData();
      toast.success(happyHour.is_active ? 'Happy Hour désactivée' : 'Happy Hour activée');
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const togglePackActive = async (pack) => {
    try {
      await axios.put(`${API}/promotions/packs/${pack.id}`, {
        is_active: !pack.is_active
      });
      fetchData();
      toast.success(pack.is_active ? 'Formule désactivée' : 'Formule activée');
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="happy-hours" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Happy Hours
          </TabsTrigger>
          <TabsTrigger value="packs" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Formules
          </TabsTrigger>
        </TabsList>

        {/* Happy Hours Tab */}
        <TabsContent value="happy-hours" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Happy Hours</h2>
                <p className="text-sm text-gray-500">Réductions automatiques sur des créneaux horaires</p>
              </div>
              <Button onClick={() => openHappyHourModal()} className="bg-emerald-500 hover:bg-emerald-600 rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Happy Hour
              </Button>
            </div>

            {happyHours.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <Zap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Aucune Happy Hour</h3>
                <p className="text-gray-500 mb-4">Créez votre première Happy Hour pour attirer plus de clients</p>
                <Button onClick={() => openHappyHourModal()} variant="outline" className="rounded-xl">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une Happy Hour
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {happyHours.map(hh => (
                  <div 
                    key={hh.id} 
                    className={`bg-white rounded-2xl border p-5 ${hh.is_active ? 'border-emerald-200' : 'border-gray-200 opacity-60'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{hh.name}</h3>
                          <Badge className={hh.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                            {hh.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge className="bg-amber-100 text-amber-700">
                            {hh.discount_type === 'percentage' ? `-${hh.discount_value}%` : 
                             hh.discount_type === 'fixed' ? `-${hh.discount_value}€` : 
                             `${hh.discount_value}€`}
                          </Badge>
                        </div>
                        {hh.description && (
                          <p className="text-sm text-gray-500 mb-2">{hh.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                          {hh.time_slots?.map((slot, i) => (
                            <span key={`${slot.day}-${slot.start_time}-${slot.end_time}`} className="bg-gray-100 px-2 py-1 rounded">
                              {DAYS.find(d => d.id === slot.day)?.label} {slot.start_time}-{slot.end_time}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={hh.is_active}
                          onCheckedChange={() => toggleHappyHourActive(hh)}
                        />
                        <Button size="icon" variant="ghost" onClick={() => openHappyHourModal(hh)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-red-500 hover:text-red-600"
                          onClick={() => setDeleteModal({ open: true, type: 'happy-hour', id: hh.id, name: hh.name })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Packs Tab */}
          <TabsContent value="packs" className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Formules & Packs</h2>
                <p className="text-sm text-gray-500">Offres combinées à prix avantageux</p>
              </div>
              <Button onClick={() => openPackModal()} className="bg-emerald-500 hover:bg-emerald-600 rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Formule
              </Button>
            </div>

            {packs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Aucune Formule</h3>
                <p className="text-gray-500 mb-4">Créez des formules pour augmenter le panier moyen</p>
                <Button onClick={() => openPackModal()} variant="outline" className="rounded-xl">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une Formule
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {packs.map(pack => (
                  <div 
                    key={pack.id} 
                    className={`bg-white rounded-2xl border p-5 ${pack.is_active ? 'border-blue-200' : 'border-gray-200 opacity-60'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{pack.name}</h3>
                          <Badge className={pack.is_active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}>
                            {pack.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          {pack.price_type === 'fixed' && pack.price && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              {(Number(pack.price) || 0).toFixed(2)}€
                            </Badge>
                          )}
                          {pack.price_type === 'discount' && pack.discount_percentage && (
                            <Badge className="bg-amber-100 text-amber-700">
                              -{pack.discount_percentage}%
                            </Badge>
                          )}
                        </div>
                        {pack.description && (
                          <p className="text-sm text-gray-500 mb-2">{pack.description}</p>
                        )}
                        {pack.fixed_items?.length > 0 && (
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                            {pack.fixed_items.map((item) => (
                              <span key={item.id} className="bg-gray-100 px-2 py-1 rounded">
                                {item.quantity}x {item.name || item.id}
                              </span>
                            ))}
                          </div>
                        )}
                        {(pack.start_time || pack.end_time) && (
                          <p className="text-xs text-gray-400 mt-2">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {pack.start_time || '00:00'} - {pack.end_time || '23:59'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={pack.is_active}
                          onCheckedChange={() => togglePackActive(pack)}
                        />
                        <Button size="icon" variant="ghost" onClick={() => openPackModal(pack)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-red-500 hover:text-red-600"
                          onClick={() => setDeleteModal({ open: true, type: 'pack', id: pack.id, name: pack.name })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

      {/* Happy Hour Modal */}
      <Dialog open={happyHourModal.open} onOpenChange={(open) => !open && setHappyHourModal({ open: false, data: null, isEdit: false })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              {happyHourModal.isEdit ? 'Modifier la Happy Hour' : 'Nouvelle Happy Hour'}
            </DialogTitle>
          </DialogHeader>
          
          {happyHourModal.data && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={happyHourModal.data.name}
                    onChange={(e) => setHappyHourModal({
                      ...happyHourModal,
                      data: { ...happyHourModal.data, name: e.target.value }
                    })}
                    placeholder="Ex: Happy Hour Café"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={happyHourModal.data.description || ''}
                    onChange={(e) => setHappyHourModal({
                      ...happyHourModal,
                      data: { ...happyHourModal.data, description: e.target.value }
                    })}
                    placeholder="Description optionnelle..."
                    className="mt-1.5"
                  />
                </div>
              </div>

              {/* Time Slots */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Créneaux horaires *</Label>
                  <Button size="sm" variant="outline" onClick={addTimeSlot}>
                    <Plus className="w-3 h-3 mr-1" />
                    Ajouter
                  </Button>
                </div>
                {happyHourModal.data.time_slots?.map((slot, index) => (
                  <div key={`slot-${slot.day}-${slot.start_time}-${index}`} className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                    <Select
                      value={slot.day}
                      onValueChange={(val) => updateTimeSlot(index, 'day', val)}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS.map(day => (
                          <SelectItem key={day.id} value={day.id}>{day.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="time"
                      value={slot.start_time}
                      onChange={(e) => updateTimeSlot(index, 'start_time', e.target.value)}
                      className="w-28"
                    />
                    <span className="text-gray-400">→</span>
                    <Input
                      type="time"
                      value={slot.end_time}
                      onChange={(e) => updateTimeSlot(index, 'end_time', e.target.value)}
                      className="w-28"
                    />
                    {happyHourModal.data.time_slots.length > 1 && (
                      <Button size="icon" variant="ghost" onClick={() => removeTimeSlot(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Discount */}
              <div className="space-y-4">
                <Label>Type de réduction</Label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setHappyHourModal({
                      ...happyHourModal,
                      data: { ...happyHourModal.data, discount_type: 'percentage' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      happyHourModal.data.discount_type === 'percentage'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Percent className="w-5 h-5 mx-auto mb-1" />
                    <p className="text-sm font-medium">Pourcentage</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setHappyHourModal({
                      ...happyHourModal,
                      data: { ...happyHourModal.data, discount_type: 'fixed' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      happyHourModal.data.discount_type === 'fixed'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Euro className="w-5 h-5 mx-auto mb-1" />
                    <p className="text-sm font-medium">Montant fixe</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setHappyHourModal({
                      ...happyHourModal,
                      data: { ...happyHourModal.data, discount_type: 'special_price' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      happyHourModal.data.discount_type === 'special_price'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Tag className="w-5 h-5 mx-auto mb-1" />
                    <p className="text-sm font-medium">Prix spécial</p>
                  </button>
                </div>
                <div>
                  <Label>
                    {happyHourModal.data.discount_type === 'percentage' ? 'Pourcentage de réduction' :
                     happyHourModal.data.discount_type === 'fixed' ? 'Montant de réduction (€)' :
                     'Prix spécial (€)'}
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max={happyHourModal.data.discount_type === 'percentage' ? 100 : undefined}
                    value={happyHourModal.data.discount_value}
                    onChange={(e) => setHappyHourModal({
                      ...happyHourModal,
                      data: { ...happyHourModal.data, discount_value: parseFloat(e.target.value) || 0 }
                    })}
                    className="mt-1.5"
                  />
                </div>
              </div>

              {/* Apply To */}
              <div className="space-y-3">
                <Label>Appliquer à</Label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setHappyHourModal({
                      ...happyHourModal,
                      data: { ...happyHourModal.data, apply_to: 'all' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      happyHourModal.data.apply_to === 'all'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <CheckCircle2 className="w-5 h-5 mx-auto mb-1" />
                    <p className="text-sm font-medium">Tout</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setHappyHourModal({
                      ...happyHourModal,
                      data: { ...happyHourModal.data, apply_to: 'categories' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      happyHourModal.data.apply_to === 'categories'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Package className="w-5 h-5 mx-auto mb-1" />
                    <p className="text-sm font-medium">Catégories</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setHappyHourModal({
                      ...happyHourModal,
                      data: { ...happyHourModal.data, apply_to: 'products' }
                    })}
                    className={`p-3 rounded-xl border-2 text-center ${
                      happyHourModal.data.apply_to === 'products'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Tag className="w-5 h-5 mx-auto mb-1" />
                    <p className="text-sm font-medium">Produits</p>
                  </button>
                </div>

                {happyHourModal.data.apply_to === 'categories' && (
                  <div className="p-3 bg-gray-50 rounded-xl space-y-2">
                    <Label className="text-sm">Sélectionnez les catégories</Label>
                    <div className="flex flex-wrap gap-2">
                      {categories.map(cat => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            const ids = happyHourModal.data.category_ids || [];
                            const newIds = ids.includes(cat.id)
                              ? ids.filter(id => id !== cat.id)
                              : [...ids, cat.id];
                            setHappyHourModal({
                              ...happyHourModal,
                              data: { ...happyHourModal.data, category_ids: newIds }
                            });
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm ${
                            (happyHourModal.data.category_ids || []).includes(cat.id)
                              ? 'bg-emerald-500 text-white'
                              : 'bg-white border text-gray-700'
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {happyHourModal.data.apply_to === 'products' && (
                  <div className="p-3 bg-gray-50 rounded-xl space-y-2">
                    <Label className="text-sm">Sélectionnez les produits</Label>
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                      {dishes.map(dish => (
                        <button
                          key={dish.id}
                          type="button"
                          onClick={() => {
                            const ids = happyHourModal.data.product_ids || [];
                            const newIds = ids.includes(dish.id)
                              ? ids.filter(id => id !== dish.id)
                              : [...ids, dish.id];
                            setHappyHourModal({
                              ...happyHourModal,
                              data: { ...happyHourModal.data, product_ids: newIds }
                            });
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm ${
                            (happyHourModal.data.product_ids || []).includes(dish.id)
                              ? 'bg-emerald-500 text-white'
                              : 'bg-white border text-gray-700'
                          }`}
                        >
                          {dish.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHappyHourModal({ open: false, data: null, isEdit: false })}>
              Annuler
            </Button>
            <Button onClick={handleSaveHappyHour} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
              {saving ? 'Sauvegarde...' : happyHourModal.isEdit ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pack Modal */}
      <Dialog open={packModal.open} onOpenChange={(open) => !open && setPackModal({ open: false, data: null, isEdit: false })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              {packModal.isEdit ? 'Modifier la Formule' : 'Nouvelle Formule'}
            </DialogTitle>
          </DialogHeader>
          
          {packModal.data && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={packModal.data.name}
                    onChange={(e) => setPackModal({
                      ...packModal,
                      data: { ...packModal.data, name: e.target.value }
                    })}
                    placeholder="Ex: Menu Midi"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={packModal.data.description || ''}
                    onChange={(e) => setPackModal({
                      ...packModal,
                      data: { ...packModal.data, description: e.target.value }
                    })}
                    placeholder="Ex: Plat + Boisson + Dessert"
                    className="mt-1.5"
                  />
                </div>
              </div>

              {/* Products */}
              <div className="space-y-3">
                <Label>Produits inclus dans le pack</Label>
                <Select onValueChange={addFixedItem}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ajouter un produit..." />
                  </SelectTrigger>
                  <SelectContent>
                    {dishes.map(dish => (
                      <SelectItem key={dish.id} value={dish.id}>
                        {dish.name} - {dish.price?.toFixed(2)}€
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {packModal.data.fixed_items?.length > 0 && (
                  <div className="space-y-2">
                    {packModal.data.fixed_items.map((item, index) => (
                      <div key={`fixed-${item.id}-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <span className="font-medium">{item.name || item.id}</span>
                        <Button size="icon" variant="ghost" onClick={() => removeFixedItem(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="space-y-4">
                <Label>Type de tarification</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPackModal({
                      ...packModal,
                      data: { ...packModal.data, price_type: 'fixed' }
                    })}
                    className={`p-4 rounded-xl border-2 text-center ${
                      packModal.data.price_type === 'fixed'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Euro className="w-6 h-6 mx-auto mb-2" />
                    <p className="font-medium">Prix fixe</p>
                    <p className="text-xs text-gray-500">Ex: 12€ le menu</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPackModal({
                      ...packModal,
                      data: { ...packModal.data, price_type: 'discount' }
                    })}
                    className={`p-4 rounded-xl border-2 text-center ${
                      packModal.data.price_type === 'discount'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Percent className="w-6 h-6 mx-auto mb-2" />
                    <p className="font-medium">Réduction</p>
                    <p className="text-xs text-gray-500">Ex: -15% sur le total</p>
                  </button>
                </div>
                
                {packModal.data.price_type === 'fixed' ? (
                  <div>
                    <Label>Prix du pack (€)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={packModal.data.price || ''}
                      onChange={(e) => setPackModal({
                        ...packModal,
                        data: { ...packModal.data, price: e.target.value }
                      })}
                      placeholder="12.00"
                      className="mt-1.5"
                    />
                  </div>
                ) : (
                  <div>
                    <Label>Pourcentage de réduction</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={packModal.data.discount_percentage || ''}
                      onChange={(e) => setPackModal({
                        ...packModal,
                        data: { ...packModal.data, discount_percentage: e.target.value }
                      })}
                      placeholder="15"
                      className="mt-1.5"
                    />
                  </div>
                )}
              </div>

              {/* Time Restrictions */}
              <div className="space-y-4">
                <Label>Restrictions horaires (optionnel)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-500">Heure de début</Label>
                    <Input
                      type="time"
                      value={packModal.data.start_time || ''}
                      onChange={(e) => setPackModal({
                        ...packModal,
                        data: { ...packModal.data, start_time: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500">Heure de fin</Label>
                    <Input
                      type="time"
                      value={packModal.data.end_time || ''}
                      onChange={(e) => setPackModal({
                        ...packModal,
                        data: { ...packModal.data, end_time: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-gray-500 mb-2 block">Jours disponibles</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map(day => (
                      <button
                        key={day.id}
                        type="button"
                        onClick={() => {
                          const days = packModal.data.available_days || [];
                          const newDays = days.includes(day.id)
                            ? days.filter(d => d !== day.id)
                            : [...days, day.id];
                          setPackModal({
                            ...packModal,
                            data: { ...packModal.data, available_days: newDays }
                          });
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm ${
                          (packModal.data.available_days || []).includes(day.id)
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Laissez vide pour tous les jours</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPackModal({ open: false, data: null, isEdit: false })}>
              Annuler
            </Button>
            <Button onClick={handleSavePack} disabled={saving} className="bg-blue-500 hover:bg-blue-600">
              {saving ? 'Sauvegarde...' : packModal.isEdit ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, type: null, id: null, name: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer <strong>"{deleteModal.name}"</strong> ? Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, type: null, id: null, name: '' })}>
              Annuler
            </Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-500 hover:bg-red-600">
              {deleting ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
